<template>
  <section
    class="bg-monthly-bonus-bg bg-cover bg-fixed bg-top bg-no-repeat h-auto bg-[#00284F]"
  >
    <div class="max-w-[1200px] mx-auto px-4 lg:px-8">
      <div class="flex flex-col items-right pt-28 pb-20 md:pt-36 lg:pt-40 font-oxanium">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-5xl sm:text-6xl font-extrabold font-golos-text">
            MONTHLY BONUS
          </h1>
          <p class="text-lg sm:text-2xl font-montserrat">CLAIM YOUR MONTHLY BONUS</p>
        </div>

        <div class="flex flex-col justify-center items-center mt-10">
          <p class="text-lg sm:text-2xl font-montserrat mb-4">
            TIME REMAINING TO CLAIM BONUS
          </p>

          <!-- Countdown Timer -->
          <div class="flex text-white items-center mt-4">
            <div
              v-for="(unit, i) in timeUnits"
              :key="i"
              class="flex items-center relative"
            >
              <!-- Timer Box -->
              <div class="flex flex-col items-center">
                <div
                  class="bg-black text-lg sm:text-6xl font-bold px-6 py-4 rounded-md min-w-[40px] sm:min-w-[80px] text-center"
                >
                  {{ unit.value }}
                </div>
                <span class="mt-2 text-sm sm:text-base tracking-wide uppercase">
                  {{ unit.label }}
                </span>
              </div>

              <!-- Connector (only if not last) -->
              <div
                v-if="i < timeUnits.length - 1"
                class="relative h-[60px] w-[16px] mx-2 flex items-center justify-center"
              >
                <div class="w-[6px] h-full rounded-md relative">
                  <div
                    class="absolute top-[5%] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-white rounded-full"
                  ></div>
                  <div
                    class="absolute bottom-[45%] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-white rounded-full"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Monthly Reward Amount Display -->
          <div
            class="mt-10 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-6 py-5 flex flex-col gap-4 shadow-lg font-orbitron"
          >
            <!-- Top: Label + Button -->
            <div class="w-full flex flex-col sm:flex-row justify-between items-center">
              <!-- Label -->
              <div
                class="text-white text-lg sm:text-base font-bold flex items-center gap-1"
              >
                <span class="hidden md:flex text-[#EEAF0E] text-[80px] mb-[2px]">•</span>
                <span class="text-white text-sm sm:text-base">Current Monthly</span>
                <span class="text-[#EEAF0E] ml-1 text-sm sm:text-base"
                  >Reward Amount</span
                >
              </div>

              <!-- Claim Button -->
              <button
                class="bg-[#1992F4] hover:bg-[#0c7bd8] transition text-white font-semibold text-sm px-5 py-3 w-full sm:w-[200px] mt-4 sm:mt-0 rounded-md flex justify-center items-center gap-2 whitespace-nowrap"
              >
                <img
                  src="@/assets/tabler-icon-moneybag.png"
                  alt="Moneybag Icon"
                  class="w-5 h-5 object-contain"
                />
                Claim Now
              </button>
            </div>

            <!-- Bottom: Digits Display -->
            <div
              class="flex items-center px-4 py-2 bg-[#FDB913] rounded-[6px] relative overflow-x-auto whitespace-nowrap"
              style="clip-path: polygon(16px 0%, 100% 0%, 100% 100%, 16px 100%, 0% 50%)"
            >
              <!-- Digits -->
              <div
                class="flex justify-center items-end w-full lg:w-auto xl:w-full gap-x-1"
              >
                <!-- Dollar Sign -->
                <div
                  class="text-black text-[32px] sm:text-[30px] md:text-[40px] lg:text-[54px] font-black mr-3 pl-1 sm:mb-2 leading-none"
                >
                  $
                </div>

                <div
                  v-for="(char, i) in rewardFormatted"
                  :key="i"
                  :class="[
                    'rounded-[4px] text-center font-black leading-none',
                    char === ',' || char === '.'
                      ? 'text-black text-[24px] sm:text-[38px] mx-[2px] sm:mx-[4px]'
                      : 'px-[4px] sm:px-[6px] py-[3px] sm:py-[4px] min-w-[20px] sm:min-w-[28px] md:min-w-[36px] text-[24px] sm:text-[30px] md:text-[40px] lg:text-[56px]',
                    i < firstActiveDigitIndex && char !== ',' && char !== '.'
                      ? 'bg-[#1A1A1A] text-[#444]'
                      : char !== ',' && char !== '.'
                      ? 'bg-[#1A1A1A] text-white'
                      : '',
                  ]"
                >
                  {{ char }}
                </div>
              </div>
            </div>
          </div>

          <!-- Reward History Section -->
          <div
            class="mt-8 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-3 sm:px-6 py-5 shadow-lg text-white font-montserrat"
          >
            <!-- Header -->
            <div class="flex items-center justify-start mb-4 gap-2">
              <span class="text-[#FDB913] text-[50px] mb-2 font-semibold">•</span>
              <h2 class="text-white text-lg sm:text-xl font-bold">
                Rewards <span class="text-[#FDB913]">History</span>
              </h2>
            </div>

            <!-- Table -->
            <div class="w-full">
              <!-- Table Header -->
              <div
                class="grid grid-cols-3 text-sm sm:text-base font-semibold text-[#cccccc] bg-[#2A2A2A] rounded-md px-4 py-3"
              >
                <div class="text-left">Month</div>
                <div class="text-center">Reward Amount</div>
                <div class="text-right">Status</div>
              </div>

              <!-- Row 1 -->
              <div
                class="grid grid-cols-3 items-center bg-[#313B44] text-white px-4 py-4 mt-2 rounded-md font-semibold"
              >
                <div class="text-left text-white font-bold">June</div>
                <div class="text-center text-[#FDB913] font-bold">$ 34,567.89</div>
                <div class="text-right text-[#7A8CA4]">Claimed</div>
              </div>

              <!-- Row 2 -->
              <div
                class="grid grid-cols-3 items-center bg-[#2C2C2C] text-white px-4 py-4 mt-2 rounded-md font-semibold"
              >
                <div class="text-left text-white font-bold">May</div>
                <div class="text-center text-[#FDB913] font-bold">$ 12,345.67</div>
                <div class="text-right text-[#7A8CA4]">Claimed</div>
              </div>

              <!-- Row 3 -->
              <div
                class="grid grid-cols-3 items-center bg-[#313B44] text-white px-4 py-4 mt-2 rounded-md font-semibold"
              >
                <div class="text-left text-white font-bold">April</div>
                <div class="text-center text-[#FDB913] font-bold">$ 10,987.65</div>
                <div class="text-right text-[#7A8CA4]">Claimed</div>
              </div>
            </div>
          </div>

          <!-- How to Claim Section -->
          <div
            class="mt-8 w-full max-w-[920px] bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-2xl px-3 sm:px-6 py-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 shadow-lg text-white font-montserrat"
          >
            <!-- Left Side: Text -->
            <div class="flex-1">
              <!-- Header row: dot + heading -->
              <div class="flex items-center gap-2 mb-3">
                <div
                  class="flex items-center justify-center w-[24px] h-[24px] sm:w-[28px] sm:h-[28px]"
                >
                  <span
                    class="hidden md:flex text-[#EEAF0E] text-[80px] mb-3 leading-none"
                    >•</span
                  >
                </div>
                <h2 class="text-lg sm:text-xl font-bold whitespace-nowrap">
                  How to Claim <span class="text-[#EEAF0E]">Monthly Bonus</span>
                </h2>
              </div>

              <!-- List -->
              <ul class="list-disc list-inside text-sm sm:text-base space-y-2 ml-4">
                <li>
                  You can claim your Monthly Bonus once per month at the end of each
                  month.
                </li>
                <li>
                  The total amount is determined by your Deposits, Withdrawls & Wager
                  during the month.
                </li>
                <li>
                  Monthly Bonus can only be claimed by Metaspins & Dailyspins Affiliates.
                </li>
                <li>
                  The Monthly Bonus will only be claimable for up to 72 hours after the
                  timer ends.
                </li>
                <li>Claim your Monthly Bonus by clicking Claim Now!</li>
              </ul>
            </div>

            <!-- Right Side: Image -->
            <div class="hidden sm:flex flex-shrink-0">
              <img
                src="@/assets/treasure-card.png"
                alt="Treasure Chest"
                class="w-[80px] h-auto sm:w-[220px] lg:w-[200px] object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import store from "@/store";
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      userToken: null,
      showConfirmRewardsModal: false,
      showClaimSuccessModal: false,
      selectedReward: null,

      loadingMilestones: true,
      displayName: null,
      picUrl: null,
      discordID: null,

      userPoints: 0,
      animatedPoints: 0,
      animatedCurrentWager: 0,

      // Countdown timer data
      targetTime: new Date(
        Date.now() +
          5 * 24 * 60 * 60 * 1000 +
          20 * 60 * 60 * 1000 +
          12 * 60 * 1000 +
          39 * 1000
      ),
      timeUnits: [
        { label: "Days", value: "00" },
        { label: "Hours", value: "00" },
        { label: "Minutes", value: "00" },
        { label: "Seconds", value: "00" },
      ],
      interval: null,
      isMobileView: window.innerWidth < 640,
    };
  },
  computed: {
    rewardFormatted() {
      const full = "000,020,000.00".split("");
      const trimmed = "20,000.00".split("");

      return this.isMobileView ? trimmed : full;
    },

    firstActiveDigitIndex() {
      const raw = this.rewardFormatted;
      for (let i = 0; i < raw.length; i++) {
        const c = raw[i];
        if (c !== "0" && c !== "," && c !== ".") return i;
      }
      return raw.length;
    },
  },
  methods: {
    updateCountdown() {
      const now = new Date();
      let diff = this.targetTime - now;

      if (diff <= 0) {
        this.timeUnits = this.timeUnits.map((u) => ({ ...u, value: "00" }));
        clearInterval(this.interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      diff %= 1000 * 60 * 60 * 24;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      diff %= 1000 * 60 * 60;
      const minutes = Math.floor(diff / (1000 * 60));
      diff %= 1000 * 60;
      const seconds = Math.floor(diff / 1000);

      this.timeUnits[0].value = String(days).padStart(2, "0");
      this.timeUnits[1].value = String(hours).padStart(2, "0");
      this.timeUnits[2].value = String(minutes).padStart(2, "0");
      this.timeUnits[3].value = String(seconds).padStart(2, "0");
    },

    init() {
      this.userToken =
        store.state.user?.accessToken || localStorage.getItem("ballyboy-access-token");

      if (this.userToken) {
        axios
          .get(process.env.VUE_APP_BASE_API + "/api/user", {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${this.userToken}`,
            },
          })
          .then((res) => {
            if (res.data) {
              this.displayName = res.data.user.username;
              this.picUrl =
                "https://cdn.discordapp.com/avatars/" +
                res.data.user.discordId +
                "/" +
                res.data.user.profileImage +
                ".png";
              this.discordID = res.data.user.discordId;
            }
          })
          .catch((error) => {
            if (error.response) {
              if (error.response.status === 401) {
                localStorage.removeItem("vuex");
                localStorage.removeItem("ballyboy-access-token");
                window.location.reload();
              } else {
                console.log(`Error: ${error.response.status}`);
              }
            } else if (error.request) {
              console.log("No response received", error.request);
            } else {
              console.log("Error", error.message);
            }
          });
      }
    },

    initUserPoints() {
      this.userToken =
        store.state.user?.accessToken || localStorage.getItem("ballyboy-access-token");
      if (this.userToken == null) {
        this.loadingMilestones = false;
        return;
      }
      axios
        .get(process.env.VUE_APP_BASE_API + "/api/user/points", {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${this.userToken}`,
          },
        })
        .then((res) => {
          if (res.data) {
            this.userPoints = res.data.points;
          }
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 401) {
              localStorage.removeItem("vuex");
              localStorage.removeItem("ballyboy-access-token");
              window.location.reload();
            } else {
              console.log(`Error: ${error.response.status}`);
            }
          } else if (error.request) {
            console.log("No response received", error.request);
          } else {
            console.log("Error", error.message);
          }
        });
    },

    initMilestone() {
      this.userToken =
        store.state.user?.accessToken || localStorage.getItem("ballyboy-access-token");
      if (this.userToken == null) {
        this.loadingMilestones = false;
        return;
      }
      axios
        .get(process.env.VUE_APP_BASE_API + "/api/user/milestones", {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${this.userToken}`,
          },
        })
        .then((res) => {
          if (res.data) {
            this.milestones = res.data.milestones[0];
            this.totalWagers = res.data.totalWagers[0] ?? 0;
            this.loadingMilestones = false;
            this.animateValues();
          }
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 401) {
              localStorage.removeItem("vuex");
              localStorage.removeItem("ballyboy-access-token");
              window.location.reload();
            } else {
              console.log(`Error: ${error.response.status}`);
            }
          } else if (error.request) {
            console.log("No response received", error.request);
          } else {
            console.log("Error", error.message);
          }
        });
    },

    cancelClaim() {
      this.selectedReward = null;
      this.showConfirmRewardsModal = false;
    },

    claimReward(milestone) {
      this.selectedReward = milestone;
      this.showConfirmRewardsModal = true;
    },

    confirmClaim() {
      this.loadingMilestones = true;
      const token = localStorage.getItem("ballyboy-access-token");

      axios
        .post(
          process.env.VUE_APP_BASE_API + "/api/user/milestones/claim",
          {
            userMilestoneId: this.selectedReward.userMilestone._id,
          },
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data) {
            this.init();
          }
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 401) {
              localStorage.removeItem("vuex");
              localStorage.removeItem("ballyboy-access-token");
              window.location.reload();
            } else {
              this.errorShow = true;
              this.errorMessage = error.response.data.message;
              console.log(`Error: ${error.response.status}`);
              setTimeout(() => {
                this.errorShow = false;
              }, 3000);
            }
          } else if (error.request) {
            console.log("No response received", error.request);
          } else {
            console.log("Error", error.message);
          }
        })
        .finally(() => {
          this.loadingMilestones = false;
          this.init();
          this.initUserPoints();
          this.initMilestone();
        });

      console.log("Reward claimed for:", this.selectedReward);
      this.$toast?.success("Reward successfully claimed!");
      this.showConfirmRewardsModal = false;
      this.showClaimSuccessModal = true;
    },

    handleResize() {
      this.isMobileView = window.innerWidth < 640;
    },

    isMobile() {
      return window.innerWidth < 1024;
    },
  },

  mounted() {
    this.init();
    this.initUserPoints();
    this.initMilestone();
    this.updateCountdown();
    this.interval = setInterval(this.updateCountdown, 1000);
    window.addEventListener("resize", this.handleResize);
    this.handleResize(); // trigger once on mount
  },

  beforeUnmount() {
    clearInterval(this.interval);
    window.removeEventListener("resize", this.$forceUpdate);
  },
});
</script>

<style scoped></style>